import {useEffect, useRef, useState} from 'react';
import {useMediaQueries} from 'presentation/pages/private';
import {QueryParamsEnum} from 'presentation/constants';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {reorderUfs} from '../utils';
import {useTheme} from 'styled-components';

const COLLAPSED_HEIGHT = 40;

export function useUfSelector(ufs: string[]) {
	const {isMobile} = useMediaQueries();
	const theme = useTheme();
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {ufCode} = SEARCH_PARAMS;
	const [orderedUfsState, setOrderedUfsState] = useState(() => reorderUfs(ufs, ufCode));
	const [isExpandedState, setIsExpandedState] = useState(false);
	const [showExpandButton, setShowExpandButton] = useState(false);
	const ufsListRef = useRef<HTMLDivElement>(null);

	const handleSelectUf = (uf: string) => {
		if (uf === ufCode) {
			setIsExpandedState(false);
			return;
		}

		setOrderedUfsState(prev => [uf, ...prev.filter(item => item !== uf)]);
		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.UF_CODE, uf);
				return prev;
			},
			{replace: true},
		);
		setIsExpandedState(false);
	};

	const handleSelectAll = () => {
		setOrderedUfsState(ufs);
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.UF_CODE);
				return prev;
			},
			{replace: true},
		);
		setIsExpandedState(false);
	};

	const toggleExpand = () => {
		setIsExpandedState(prev => !prev);
	};

	useEffect(() => {
		setOrderedUfsState(reorderUfs(ufs, ufCode));
	}, [ufs, ufCode]);

	useEffect(() => {
		const element = ufsListRef.current;
		if (!element) return undefined;

		const checkOverflow = () => setShowExpandButton(element.scrollHeight > COLLAPSED_HEIGHT);
		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [orderedUfsState]);

	return {
		ufCode,
		ufsListRef,
		orderedUfsState,
		showExpandButton,
		isExpandedState,
		handleSelectUf,
		handleSelectAll,
		toggleExpand,
		isMobile,
		theme,
	};
}
